<template>
  <div class="hidden lg:flex mb-6 gap-2 items-center">
    <Popover v-model:open="filterOpen">
      <div class="flex relative">
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="lg"
            :class="[
              'gap-2 transition-none',
              filterOpen ? 'rounded-b-none border-r-0 border-b border-b-white shadow-none' : '',
            ]"
          >
            Filter
            <ChevronDown :class="filterOpen ? 'rotate-180' : ''" :size="16" />
          </Button>
        </PopoverTrigger>
        <div
          v-if="filterOpen"
          class="h-12 border-l rounded-bl-xl w-5 mt-[7px] -ml-px border-b absolute -right-5 z-20"
        />
      </div>
      <PopoverContent
        class="w-[600px] p-6 rounded-tl-none z-10 duration-75"
        align="start"
        side="bottom"
        :sideOffset="6"
        :avoidCollisions="false"
        sticky="always"
      >
        <div class="bg-white h-4 absolute left-0 -top-2 z-10 w-6 border-l rounded-r-xl" />
        <div class="bg-white h-px absolute left-1 top-0 z-10 w-[120px]" />
        <div class="grid grid-cols-2 gap-x-8 gap-y-4">
          <div v-for="category in sortedCategories" :key="category.name" class="flex items-center gap-2">
            <Checkbox :id="`cat-${category.name}`" v-model="category.selected" />
            <label :for="`cat-${category.name}`" class="text-sm cursor-pointer text-grey-light-700">
              {{ category.name }}
            </label>
          </div>
        </div>
      </PopoverContent>
    </Popover>

    <Popover v-model:open="sortOpen">
      <div class="flex relative">
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="lg"
            :class="[
              'gap-2 transition-none',
              sortOpen ? 'rounded-b-none border-r-0 border-b border-b-white shadow-none' : '',
            ]"
          >
            Sort by
            <ChevronDown :class="sortOpen ? 'rotate-180' : ''" :size="16" />
          </Button>
        </PopoverTrigger>
        <div v-if="sortOpen" class="h-12 border-l rounded-bl-xl w-5 mt-[7px] -ml-px border-b absolute -right-5 z-20" />
      </div>
      <PopoverContent
        class="w-[400px] p-6 rounded-tl-none z-10 duration-75"
        align="start"
        side="bottom"
        :sideOffset="6"
        :avoidCollisions="false"
        sticky="always"
      >
        <div class="bg-white h-4 absolute left-0 -top-2 z-10 w-6 border-l rounded-r-xl" />
        <div class="bg-white h-px absolute left-1 top-0 z-10 w-[120px]" />
        <ul>
          <li
            v-for="opt in sortOptions"
            :key="opt.value"
            @click="handleSort(opt.value)"
            class="p-4 hover:cursor-pointer hover:bg-grey-light-50 rounded-lg transition-colors"
          >
            {{ opt.label }}
          </li>
        </ul>
      </PopoverContent>
    </Popover>

    <div class="flex gap-2 w-full overflow-y-auto">
      <template v-if="selectedCategories.length > 0">
        <div
          v-for="cat in selectedCategories"
          :key="cat"
          class="h-10 pl-4 pr-2 rounded-lg bg-brand-50 text-brand-700 flex items-center whitespace-nowrap"
        >
          <span class="font-medium text-sm">{{ cat }}</span>
          <div class="p-2 hover:bg-brand-200 cursor-pointer rounded-lg ml-1" @click="removeCategory(cat)">
            <X class="h-4 w-4" />
          </div>
        </div>
        <Button
          @click="clearAllCategories"
          variant="ghost"
          class="h-10 flex items-center hover:bg-brand-50 text-brand-700 hover:text-brand-700"
        >
          Clear all
        </Button>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useShopsStore } from '@/stores/shops';
import { ChevronDown, X } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

const shopsStore = useShopsStore();
const { selectedCategories, filterCategories } = storeToRefs(shopsStore);

const filterOpen = ref(false);
const sortOpen = ref(false);

const sortOptions = [
  { label: 'A-Z', value: 'a-z' as const },
  { label: 'Z-A', value: 'z-a' as const },
  { label: 'Newest', value: 'newest' as const },
];

const sortedCategories = computed(() =>
  [...filterCategories.value].sort((a, b) => {
    if (a.name === 'Popular') return -1;
    if (b.name === 'Popular') return 1;
    return a.name.localeCompare(b.name);
  }),
);

const handleSort = (sort: 'a-z' | 'z-a' | 'newest') => {
  shopsStore.setSortBy(sort);
  sortOpen.value = false;
};

const removeCategory = (name: string) => {
  const cat = filterCategories.value.find((c) => c.name === name);
  if (cat) cat.selected = false;
};

const clearAllCategories = () => {
  filterCategories.value.forEach((c) => (c.selected = false));
};
</script>
